/**
 * Pointer + keyboard interactions for the hand-rolled dashboard grid (G16).
 * Window-level pointermove/pointerup listeners (not pointer capture) so a fast
 * drag that leaves the handle keeps tracking — same pattern as
 * `visual-query-editor/compact/useResizableBox.ts`. Listeners bind
 * synchronously on drag start and are torn down on pointerup, pointercancel,
 * window blur, Escape, or unmount (C1). Layout math is delegated to the pure
 * `gridLayout.ts` engine; this hook only translates pixels → grid cells,
 * compensating for scroll of the grid's scroll container mid-drag.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GRID_COLS,
  keyboardMove,
  keyboardResize,
  moveTile,
  resizeTile,
  type GridDirection,
  type GridItem,
} from '../utils/gridLayout';

export const GRID_GAP = 12;
/**
 * Grid row unit. 64px keeps the grid dense enough that a stat tile at its
 * default h:2 (2×64 + gap = 140px) reads as a compact half-tile next to an
 * h:4 chart/table (292px ≈ the visual weight h:3 had at the old 84px unit).
 */
export const GRID_ROW_HEIGHT = 64;

type DragState = {
  mode: 'move' | 'resize';
  id: string;
  startX: number;
  startY: number;
  startScrollTop: number;
  origin: GridItem;
  cellWidth: number;
};

const sameLayout = (a: GridItem[], b: GridItem[]): boolean =>
  a.length === b.length &&
  a.every((item, i) => {
    const other = b[i];
    return item.x === other.x && item.y === other.y && item.w === other.w && item.h === other.h;
  });

const DIRECTION_BY_KEY: Record<string, GridDirection> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
};

export function useGridInteractions(
  baseItems: GridItem[],
  enabled: boolean,
  onCommit: (items: GridItem[]) => unknown,
  onKeyboardChange?: (item: GridItem) => void,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<GridItem[] | null>(null);
  // Raw sub-cell pixel offset of the tile being MOVED — drives its transform so
  // it floats with the pointer instead of snapping its grid cell every frame.
  const [dragDelta, setDragDelta] = useState<{ dx: number; dy: number } | null>(null);
  const dragRef = useRef<DragState | null>(null);
  // Latest values for the window listeners without re-binding per drag frame.
  const baseRef = useRef(baseItems);
  baseRef.current = baseItems;
  const previewRef = useRef(preview);
  previewRef.current = preview;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const onKeyboardChangeRef = useRef(onKeyboardChange);
  onKeyboardChangeRef.current = onKeyboardChange;

  const scrollParent = () => containerRef.current?.parentElement ?? null;

  const handleMove = useCallback((e: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    // Wheel-scrolling mid-drag moves the grid content under a stationary
    // pointer — fold the scroll delta into the vertical distance.
    const scrollDelta = (scrollParent()?.scrollTop ?? drag.startScrollTop) - drag.startScrollTop;
    const rawDx = e.clientX - drag.startX;
    const rawDy = e.clientY - drag.startY + scrollDelta;
    const dx = Math.round(rawDx / (drag.cellWidth + GRID_GAP));
    const dy = Math.round(rawDy / (GRID_ROW_HEIGHT + GRID_GAP));
    const { origin } = drag;
    const next =
      drag.mode === 'move'
        ? moveTile(baseRef.current, drag.id, { x: origin.x + dx, y: origin.y + dy })
        : resizeTile(baseRef.current, drag.id, { w: origin.w + dx, h: origin.h + dy });
    // No per-frame RE-LAYOUT: the snapped reflow only re-renders when it
    // actually changes (a cell-boundary crossing), not on every sub-cell pixel.
    if (!previewRef.current || !sameLayout(next, previewRef.current)) setPreview(next);
    // A move floats via a composited transform (not a layout write); a resize
    // is inherently a layout change, so it has no free-floating transform.
    if (drag.mode === 'move') setDragDelta({ dx: rawDx, dy: rawDy });
  }, []);

  const endDrag = useCallback(
    (commit: boolean) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setDragDelta(null);
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUpRef.current);
      window.removeEventListener('pointercancel', handleCancelRef.current);
      window.removeEventListener('blur', handleCancelRef.current);
      window.removeEventListener('keydown', handleKeyCancelRef.current);
      const next = previewRef.current;
      if (commit && next && !sameLayout(next, baseRef.current)) {
        // Hold the preview until persistence settles — clearing it first
        // renders the stale layout for a frame (snap-back flash); on failure
        // the store reload refreshes baseItems and this clear rolls back.
        Promise.resolve(onCommitRef.current(next)).finally(() => setPreview(null));
      } else {
        setPreview(null);
      }
    },
    [handleMove],
  );
  const endDragRef = useRef(endDrag);
  endDragRef.current = endDrag;

  const handleUpRef = useRef(() => endDragRef.current(true));
  const handleCancelRef = useRef(() => endDragRef.current(false));
  const handleKeyCancelRef = useRef((e: KeyboardEvent) => {
    if (e.key === 'Escape') endDragRef.current(false);
  });

  const startDrag = useCallback(
    (mode: 'move' | 'resize', id: string) => (e: React.PointerEvent) => {
      if (!enabled || e.button !== 0 || dragRef.current) return;
      const container = containerRef.current;
      const origin = baseRef.current.find((i) => i.id === id);
      if (!container || !origin) return;
      e.preventDefault();
      const cellWidth = (container.clientWidth - GRID_GAP * (GRID_COLS - 1)) / GRID_COLS;
      dragRef.current = {
        mode,
        id,
        startX: e.clientX,
        startY: e.clientY,
        startScrollTop: scrollParent()?.scrollTop ?? 0,
        origin,
        cellWidth,
      };
      setPreview(baseRef.current);
      // Bind synchronously — waiting for a state-driven effect can miss the
      // first pointermove of a fast drag.
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUpRef.current);
      window.addEventListener('pointercancel', handleCancelRef.current);
      window.addEventListener('blur', handleCancelRef.current);
      window.addEventListener('keydown', handleKeyCancelRef.current);
    },
    [enabled, handleMove],
  );

  // Safety net: drop listeners if the component unmounts mid-drag.
  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUpRef.current);
      window.removeEventListener('pointercancel', handleCancelRef.current);
      window.removeEventListener('blur', handleCancelRef.current);
      window.removeEventListener('keydown', handleKeyCancelRef.current);
    },
    [handleMove],
  );

  const onTileKeyDown = useCallback(
    (id: string) => (e: React.KeyboardEvent) => {
      if (!enabled) return;
      // Arrows inside descendant controls (kebab, future S5/S6 editors) must
      // not move the tile — only handle keys targeted at the wrapper itself.
      if (e.target !== e.currentTarget) return;
      const direction = DIRECTION_BY_KEY[e.key];
      if (!direction) return;
      e.preventDefault();
      e.stopPropagation();
      const next = e.shiftKey
        ? keyboardResize(baseRef.current, id, direction)
        : keyboardMove(baseRef.current, id, direction);
      if (!sameLayout(next, baseRef.current)) {
        onCommitRef.current(next);
        const moved = next.find((i) => i.id === id);
        if (moved) onKeyboardChangeRef.current?.(moved);
      }
    },
    [enabled],
  );

  return {
    containerRef,
    items: preview ?? baseItems,
    draggingId: dragRef.current?.id ?? null,
    /** 'move' floats the dragged tile via transform; 'resize' reflows in place. */
    dragMode: dragRef.current?.mode ?? null,
    /** Pixel offset for the moved tile's transform (null unless a move is live). */
    dragDelta,
    startDrag,
    onTileKeyDown,
  };
}
